import React, { useState, useEffect } from 'react';

export default function Settings({ onClose }) {
  const [masterVolume, setMasterVolume] = useState(100);
  const [musicVolume, setMusicVolume] = useState(50);
  const [effectsVolume, setEffectsVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [textSpeed, setTextSpeed] = useState(100);
  const [typeText, setTypeText] = useState(true);
  const [activeTab, setActiveTab] = useState('audio');

  useEffect(() => {
    setMasterVolume(window.gameAudio.savedVolumes.master * 100);
    setMusicVolume(window.gameAudio.savedVolumes.music * 100);
    setEffectsVolume(window.gameAudio.savedVolumes.effects * 100);
    setIsMuted(window.gameAudio.isMuted);
    setTextSpeed(window.gameLogic.getTypingSpeed());
    setTypeText(window.gameLogic.getToTypeText());
  }, []);

  const handleMasterVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMasterVolume(value);
    window.gameAudio.setMasterVolume(value / 100);
  };

  const handleMusicVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMusicVolume(value);
    window.gameAudio.setMusicVolume(value / 100);
  };

  const handleEffectsVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setEffectsVolume(value);
    window.gameAudio.setEffectsVolume(value / 100);
  };

  const handleMuteChange = (e) => {
    const muted = e.target.checked;
    setIsMuted(muted);
    window.gameAudio.setMute(muted);
  };

  const handleTextSpeedChange = (e) => {
    const value = parseInt(e.target.value);
    setTextSpeed(value);
    window.gameLogic.setTypingSpeed(value);
  };

  const handleTypeTextChange = (e) => {
    const value = e.target.checked;
    setTypeText(value);
    window.gameLogic.setToTypeText(value);
  };

  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none" />
      
      <div className="relative w-[800px] h-[600px] bg-white/10 backdrop-blur-md rounded-3xl overflow-hidden border border-white/20 shadow-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/10 via-transparent to-pink-100/10 pointer-events-none" />
        
        <div className="relative p-8 h-full">
          <div className="flex flex-col h-full">
            <div className="mb-8 pointer-events-none">
              <h2 className="font-['Cormorant_Garamond'] text-4xl text-rose-50/90 mb-2">Settings</h2>
              <div className="w-32 h-0.5 bg-gradient-to-r from-rose-200/50 to-pink-200/50" />
            </div>

            <div className="flex space-x-4 mb-8">
              <button
                onClick={() => setActiveTab('audio')}
                className={`px-8 py-2 rounded-full font-['Quicksand'] transition-all duration-300 ${
                  activeTab === 'audio'
                    ? 'bg-white/20 text-white shadow-lg shadow-rose-500/20'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                Audio
              </button>
              <button
                onClick={() => setActiveTab('game')}
                className={`px-8 py-2 rounded-full font-['Quicksand'] transition-all duration-300 ${
                  activeTab === 'game'
                    ? 'bg-white/20 text-white shadow-lg shadow-rose-500/20'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                Game
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {activeTab === 'audio' && (
                <div className="space-y-8 pr-4">
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="text-rose-50/90 font-['Quicksand']">Master Volume</label>
                      <input 
                        type="range"
                        value={masterVolume}
                        onChange={handleMasterVolumeChange}
                        className="w-full custom-slider"
                        min="0"
                        max="100"
                      />
                      <div className="text-right text-rose-50/70 text-sm">{masterVolume}%</div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-rose-50/90 font-['Quicksand']">Music Volume</label>
                      <input 
                        type="range"
                        value={musicVolume}
                        onChange={handleMusicVolumeChange}
                        className="w-full custom-slider"
                        min="0"
                        max="100"
                      />
                      <div className="text-right text-rose-50/70 text-sm">{musicVolume}%</div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-rose-50/90 font-['Quicksand']">Effects Volume</label>
                      <input 
                        type="range"
                        value={effectsVolume}
                        onChange={handleEffectsVolumeChange}
                        className="w-full custom-slider"
                        min="0"
                        max="100"
                      />
                      <div className="text-right text-rose-50/70 text-sm">{effectsVolume}%</div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-rose-50/90 font-['Quicksand']">Mute All</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={isMuted}
                          onChange={handleMuteChange}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-rose-400/50"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'game' && (
                <div className="space-y-8 pr-4">
                  <div className="space-y-3">
                    <label className="text-rose-50/90 font-['Quicksand']">Text Speed</label>
                    <input 
                      type="range"
                      value={textSpeed}
                      onChange={handleTextSpeedChange}
                      className="w-full custom-slider"
                      min="1"
                      max="200"
                    />
                    <div className="text-right text-rose-50/70 text-sm">{textSpeed}%</div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-rose-50/90 font-['Quicksand']">Type Text</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox"
                        checked={typeText}
                        onChange={handleTypeTextChange}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-rose-400/50"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-rose-50/90 font-['Quicksand']">Auto-Save</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-rose-400/50"></div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button 
            onClick={onClose}
            className="absolute top-6 right-6 text-rose-50/70 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .custom-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          outline: none;
        }
        .custom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
        }
        .custom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
